/* eslint-disable react-hooks/exhaustive-deps */
import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios'
import { PropsWithChildren, useEffect, useState } from 'react'

import { experienceBuilderApi } from 'api'
import { loginRequest } from 'auth/config'

const apiInstances = [experienceBuilderApi]

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean
}

interface ApiProviderProps extends PropsWithChildren {
  getToken?: () => Promise<string> // ← from root via customProps
}

const applyAuthorizationHeader = (config: InternalAxiosRequestConfig, token: string): InternalAxiosRequestConfig => {
  config.headers.set('Authorization', `Bearer ${token}`)
  config.headers.set('Client', 'playground-dev-fe')
  config.headers.set('X-Tenant-Id', process.env.REACT_APP_X_TENANT_ID ?? '')
  return config
}

export const ApiProvider = ({ children, getToken }: ApiProviderProps) => {
  const { instance, accounts } = useMsal()
  const [isReady, setIsReady] = useState(false)

  const getMsalToken = async (): Promise<string | null> => {
    // if root passed getToken — use it directly, no MSAL init needed
    if (getToken) {
      const token = await getToken()
      return token || null
    }

    // fallback — child running standalone
    const account = accounts[0] ?? instance.getAllAccounts()[0]
    if (!account) return null

    try {
      const response = await instance.acquireTokenSilent({ ...loginRequest, account })
      return response.accessToken || null
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        const loginRoot = process.env.REACT_APP_ROOT_URL?.trim()
        if (loginRoot) window.location.assign(`${loginRoot}/login`)
      }
      return null
    }
  }

  useEffect(() => {
    const account = accounts[0] ?? instance.getAllAccounts()[0]

    // if getToken from root is available, skip account check —
    // root already verified the user is logged in
    if (!getToken && !account) {
      console.warn('[ApiProvider] no accounts and no getToken from root')
      return
    }

    const cleanupHandlers: Array<() => void> = []

    for (const client of apiInstances) {
      const requestId = client.client.interceptors.request.use(
        async config => {
          const token = await getMsalToken()
          if (!token) {
            console.warn('[ApiProvider] no token available')
            return config
          }
          return applyAuthorizationHeader(config, token)
        },
        error => Promise.reject(error),
      )

      const responseId = client.client.interceptors.response.use(
        response => response,
        async error => {
          const axiosError = error as AxiosError
          const originalRequest = axiosError.config as RetryableRequestConfig | undefined

          if (!originalRequest || axiosError.response?.status !== 401 || originalRequest._retry) {
            return Promise.reject(error)
          }

          const freshToken = await getMsalToken()
          if (!freshToken) return Promise.reject(error)

          originalRequest._retry = true
          applyAuthorizationHeader(originalRequest, freshToken)
          return client.client.request(originalRequest as AxiosRequestConfig)
        },
      )

      cleanupHandlers.push(() => client.client.interceptors.request.eject(requestId))
      cleanupHandlers.push(() => client.client.interceptors.response.eject(responseId))
    }

    setIsReady(true)

    return () => cleanupHandlers.forEach(c => c())
  }, [instance, accounts, getToken])

  if (!isReady) return null

  return <>{children}</>
}
